import { useEffect, useRef } from "react";
import { postMessage } from "core/utils/tracker";

// Throttle interval in milliseconds (videoState updates every 100ms)
const THROTTLE_INTERVAL = 500;

/**
 * Hook to broadcast videoState changes via postMessage
 * Uses throttling to avoid spamming the parent window with updates
 *
 * @param {Object} videoState - Current video state
 * @param {boolean} triggerPostMessage - Whether to send postMessages
 * @param {string} [contentId] - Content ID to identify the video player
 */
export default function useVideoStatePostMessage({
  videoState,
  triggerPostMessage,
  contentId,
}) {
  const lastSendTimeRef = useRef(0);
  const lastStatusRef = useRef(null);

  useEffect(() => {
    if (!triggerPostMessage || !videoState) return;

    const now = Date.now();
    const timeSinceLastSend = now - lastSendTimeRef.current;
    const statusChanged = lastStatusRef.current !== videoState.status;

    // Always send immediately on critical state changes
    const shouldSendImmediately =
      statusChanged ||
      videoState.isEnded ||
      videoState.seeking ||
      lastSendTimeRef.current === 0; // First send

    // Throttle frequent updates
    const shouldThrottle = timeSinceLastSend >= THROTTLE_INTERVAL;

    if (shouldSendImmediately || shouldThrottle) {
      postMessage({
        type: "videoState",
        data: {
          videoId: contentId,
          currentTime: videoState.currentTime,
          duration: videoState.duration,
          paused: videoState.paused,
          ended: videoState.ended,
          muted: videoState.muted,
          volume: videoState.volume,
          status: videoState.status,
          isStarted: videoState.isStarted,
          isEnded: videoState.isEnded,
          isLoading: videoState.isLoading,
          buffered: videoState.buffered,
          seeking: videoState.seeking,
          playbackRate: videoState.playbackRate,
          live: videoState.live,
        },
      });

      lastSendTimeRef.current = now;
      lastStatusRef.current = videoState.status;
    }
  }, [videoState, triggerPostMessage, contentId]);
}
